import React, {
  FC,
  PropsWithChildren,
  useState,
  useRef,
  useEffect,
} from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PaymenSubmissionModalCloseWrapper,
  PaymenSubmissionModalItem,
  PaymenSubmissionModalItemsWrapper,
  PaymenSubmissionModalTitle,
  PaymenSubmissionModalTitleLine,
} from './styles';
import { usePatientContext } from '../../contexts/patient-context';
import { usePaymentConfig } from '../hooks/payment-config-hook';
import { theme } from '../../utils/theme';
import { Colors } from '../../assets/styles/colors';
import {
  PaymentConfigCategory,
  PaymentConfigSubcategory,
} from '../../models/paymentConfig';
import { usePaymentSubmissionsContext } from '../../contexts/payment-submissions-context';
import { Wrapper } from '../payment-page/styles';
import { PaymentSubmissionModalType } from './payment-submission-modal.type';
import { getModalTitle } from './get-modal-title';
import AddRequestFormComponent from './add-request-form';

const PaymentSubmissionPageComponent: FC<PropsWithChildren> = () => {
  const navigate = useNavigate();

  const hideDialogTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<PaymentSubmissionModalType | null>(
    null,
  );

  const { patient } = usePatientContext();

  const {
    data: { paymentCategories, paymentSubcategories },
    fetchCategories,
    fetchSubcategories,
  } = usePaymentConfig();

  const { t } = useTranslation();

  const {
    isSavingSubmission,
    expenses,
    resetExpenses,
    selectedPaymentCategory,
    selectedPaymentSubcategory,
    setSelectedPaymentCategory,
    setSelectedPaymentSubcategory,
    save: saveSubmission,
    setIsSavingSubmission,
  } = usePaymentSubmissionsContext();

  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const onClickPaymentCategoryItem = async (reason: PaymentConfigCategory) => {
    if (isSavingSubmission) return;
    fetchSubcategories(patient?.protocolId as string, reason.id);
    setSelectedPaymentCategory(reason);
    setSelectedPaymentSubcategory(null);
    setShowModal(false);
  };

  const onClickPaymentSubcategoryItem = (type: PaymentConfigSubcategory) => {
    if (isSavingSubmission) return;
    setSelectedPaymentSubcategory(type);
    setShowModal(false);
  };

  const openDialog = (type: PaymentSubmissionModalType) => {
    if (hideDialogTimerRef.current) {
      clearTimeout(hideDialogTimerRef.current);
    }

    setModalType(type);
    setShowModal(true);
  };

  const onClickPaymentCategoryField = () => {
    openDialog(PaymentSubmissionModalType.PaymentCategory);
  };

  const onClickPaymentSubcategoryField = () => {
    if (selectedPaymentCategory) {
      openDialog(PaymentSubmissionModalType.PaymentSubcategory);
    }
  };

  const handleDialogClose = () => {
    setShowModal(false);
    hideDialogTimerRef.current = setTimeout(() => {
      setModalType(null);
    }, 1000);
  };

  const onClickSaveSubmission = async () => {
    if (
      !patient?.protocolId ||
      !selectedPaymentCategory ||
      !selectedPaymentSubcategory
    ) {
      toast(t('Submission creation failed'));
      return;
    }

    setIsSavingSubmission(true);
    try {
      await saveSubmission(
        patient.protocolId,
        selectedPaymentCategory,
        selectedPaymentSubcategory,
        expenses,
      );
      toast(t('Submission created successfully!'));
      navigate('/');
      setSelectedPaymentCategory(null);
      setSelectedPaymentSubcategory(null);
      resetExpenses();
    } catch (err) {
      toast(t('Submission creation failed')); 
    } finally {
      setIsSavingSubmission(false);
    }
  };

  useEffect(() => {
    if (patient?.protocolId) {
      fetchCategories(patient?.protocolId);
    }
    if (
      patient?.protocolId &&
      selectedPaymentCategory?.id &&
      selectedPaymentSubcategory?.id
    ) {
      fetchSubcategories(
        patient?.protocolId as string,
        selectedPaymentCategory?.id as string,
      );
    }
  }, []);

  const getModalForm = (
    type: PaymentSubmissionModalType,
  ): JSX.Element | JSX.Element[] | undefined | null => {
    switch (type) {
      case PaymentSubmissionModalType.PaymentSubcategory:
        return paymentSubcategories?.map((paymentSubcategory) => (
          <PaymenSubmissionModalItem
            key={paymentSubcategory.id}
            selected={paymentSubcategory.id === selectedPaymentSubcategory?.id}
            onClick={() => {
              onClickPaymentSubcategoryItem(paymentSubcategory);
            }}
          >
            {paymentSubcategory.name}
          </PaymenSubmissionModalItem>
        ));
      case PaymentSubmissionModalType.PaymentCategory:
        return paymentCategories?.map((paymentCategory) => (
          <PaymenSubmissionModalItem
            key={paymentCategory.name}
            selected={paymentCategory.name === selectedPaymentCategory?.name}
            onClick={() => {
              onClickPaymentCategoryItem(paymentCategory);
            }}
          >
            {paymentCategory.name}
          </PaymenSubmissionModalItem>
        ));
      default:
        return null;
    }
  };

  const onClickAddExpenseButton = () => {
    navigate('/payment/submission/expense');
  };

  return (
    <Wrapper>
      <AddRequestFormComponent
        expenses={expenses}
        isSavingSubmission={isSavingSubmission}
        selectedPaymentCategory={selectedPaymentCategory}
        selectedPaymentSubcategory={selectedPaymentSubcategory}
        onClickPaymentCategoryField={onClickPaymentCategoryField}
        onClickPaymentSubcategoryField={onClickPaymentSubcategoryField}
        onClickAddExpenseButton={onClickAddExpenseButton}
        onClickSaveSubmission={onClickSaveSubmission}
      />
      <Dialog
        open={showModal}
        onClose={handleDialogClose}
        fullScreen={fullScreen}
        PaperProps={{
          style: fullScreen
            ? {
                height: '97%',
                transform: 'translateY(3%)',
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                paddingTop: 16,
              }
            : {},
        }}
      >
        <PaymenSubmissionModalCloseWrapper
          style={
            fullScreen
              ? {
                  top: 28,
                  right: 20,
                }
              : {}
          }
        >
          <CloseIcon onClick={handleDialogClose} htmlColor={Colors.Neutral05} />
        </PaymenSubmissionModalCloseWrapper>
        <DialogTitle>
          <PaymenSubmissionModalTitle>
            {t(getModalTitle(modalType!))}
            <PaymenSubmissionModalTitleLine />
          </PaymenSubmissionModalTitle>
        </DialogTitle>
        <DialogContent>
          <PaymenSubmissionModalItemsWrapper>
            {getModalForm(modalType!)}
          </PaymenSubmissionModalItemsWrapper>
        </DialogContent>
      </Dialog>
    </Wrapper>
  );
};

export default PaymentSubmissionPageComponent;
